/**
 * Agent A6b — Options Advisor (V85-4-4 / gap E4, R-85-05).
 *
 * Given a set of blueprint gaps and the reference architecture, populates
 * remediation options citing fit rules from the reference architecture.
 *
 * R-85-05 rules enforced:
 *   - Options cite known_implementations from the RA — no invented products
 *   - Fit level derived from fit rules, not guessed
 *   - Effort bands come from the RA slot, not the LLM
 *   - No invented coverage: if the RA has no implementation for a slot,
 *     the option says so rather than fabricating one
 *   - Unknowns are explicit ("no known implementation")
 *
 * Runs through the V85-4-0 agent harness. Output writes to
 * `proposed_changes` queue.
 */
import { defineTemplate, derive, field } from '../egress/data-classes.js';
import { LlmRefusedError } from '../egress/gateway.js';
// ---- Agent definition -------------------------------------------------------
export const A6B_DEFINITION = {
    agent_id: 'A6b',
    prompt_version: '2026.09.20',
    queue_target: 'proposed_changes',
    schema_version: '1.0',
};
export const A6B_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['options_by_slot', 'slots_without_options', 'summary'],
    properties: {
        options_by_slot: { type: 'object' },
        slots_without_options: { type: 'array' },
        summary: { type: 'string' },
    },
};
// ---- Deterministic option generation ----------------------------------------
/**
 * Classify an implementation name against fit rules, matching the
 * pattern from blueprint-planner-service.ts. Known implementations in
 * the RA are native by default.
 */
function classifyImpl(implName, rule) {
    if (!rule)
        return 'native';
    const lower = implName.toLowerCase().replace(/\s+/g, '_');
    if (rule.substitute_signals.some((s) => s.toLowerCase().includes(lower) ||
        lower.includes(s.toLowerCase().replace(/_detected$|_connector$/, '')))) {
        return 'substitute';
    }
    if (rule.partial_signals.some((s) => s.toLowerCase().includes(lower) ||
        lower.includes(s.toLowerCase().replace(/_detected$|_connector$/, '')))) {
        return 'partial';
    }
    return 'native';
}
const FIT_RANK = {
    native: 3,
    partial: 2,
    substitute: 1,
};
/**
 * Generate remediation options deterministically from the RA. Pure function.
 */
export function generateDeterministicOptions(input) {
    const slotIndex = new Map();
    for (const s of input.ra_slots)
        slotIndex.set(s.slot_id, s);
    const optionsBySlot = {};
    const slotsWithoutOptions = [];
    for (const gap of input.gaps) {
        const slot = slotIndex.get(gap.slot_id);
        if (!slot) {
            slotsWithoutOptions.push(gap.slot_id);
            continue;
        }
        const rule = input.fit_rules[gap.slot_id];
        const deadline = input.regulatory_deadlines?.[gap.slot_id] ?? null;
        if (slot.known_implementations.length === 0) {
            slotsWithoutOptions.push(gap.slot_id);
            continue;
        }
        const options = slot.known_implementations.map((impl) => ({
            slot_id: gap.slot_id,
            product_name: impl,
            fit_level: classifyImpl(impl, rule),
            effort_band: gap.effort_band,
            rationale: `Known implementation from reference architecture slot "${slot.slot_id}" (${slot.role})`,
            ra_source: `ra:${slot.slot_id}`,
            regulatory_deadline: deadline,
        }));
        // Sort: native first, then partial, then substitute
        options.sort((a, b) => FIT_RANK[b.fit_level] - FIT_RANK[a.fit_level]);
        optionsBySlot[gap.slot_id] = options;
    }
    const totalOptions = Object.values(optionsBySlot).reduce((n, opts) => n + opts.length, 0);
    return {
        options_by_slot: optionsBySlot,
        slots_without_options: slotsWithoutOptions,
        summary: `${totalOptions} remediation option(s) across ${Object.keys(optionsBySlot).length} gap slot(s). ` +
            `${slotsWithoutOptions.length} slot(s) have no known implementation in the reference architecture.`,
    };
}
// ---- Agent function ---------------------------------------------------------
export const optionsAdvisorAgent = async (llm, input) => {
    const data = input.data;
    // 1. Deterministic option generation from RA
    const result = generateDeterministicOptions(data);
    // 2. Optionally enrich rationales with LLM (graceful degradation)
    try {
        // ADR-0073: through the egress gateway; a refusal keeps the deterministic options.
        const r = await llm.run({
            task: 'agent.a6b.options', template: A6B_TEMPLATE, slots: promptSlots(data, result),
            schemaHint: A6B_OUTPUT_SCHEMA,
        });
        if (r.status !== 'ok')
            throw new LlmRefusedError(r);
        const llmResult = { data: r.output };
        if (llmResult.data?.slot_rationales) {
            for (const sr of llmResult.data.slot_rationales) {
                const slotOptions = result.options_by_slot[sr.slot_id];
                if (slotOptions) {
                    // Enrich each option's rationale with LLM insight
                    for (const opt of slotOptions) {
                        opt.rationale = `${opt.rationale}. ${sr.rationale}`;
                    }
                }
            }
            if (llmResult.data.summary) {
                result.summary = llmResult.data.summary;
            }
        }
    }
    catch {
        // LLM failure: deterministic options are sufficient
        result.summary = `Deterministic-only (LLM unavailable): ${result.summary}`;
    }
    const provenance = {
        options_by_slot: {
            source: 'reference-architecture-fit-rules',
            span: `${Object.keys(result.options_by_slot).length} slots with options`,
            confidence: 0.95,
        },
        slots_without_options: {
            source: 'reference-architecture',
            span: result.slots_without_options.join(', ') || 'none',
            confidence: 1.0,
        },
        summary: {
            source: 'deterministic-analysis',
            confidence: 0.9,
        },
    };
    const output = {
        target: 'proposed_changes',
        proposal: {
            options_by_slot: result.options_by_slot,
            slots_without_options: result.slots_without_options,
            summary: result.summary,
        },
        provenance,
    };
    return output;
};
/** ADR-0073: the A6b prompt as a reviewed template; gaps and options are tenant data. */
const A6B_PROMPT = [
    'You are an AI governance remediation advisor. Review the following gaps and their deterministic options.',
    'Provide a brief rationale for each slot explaining WHY the recommended options fit.',
    'Do NOT invent products not in the reference architecture. Do NOT change fit levels.',
    '',
    '## Gaps',
    '{gaps}',
    '',
    '## Deterministic Options',
    '{options}',
    '',
    'Return JSON: { "slot_rationales": [{ "slot_id", "rationale" }], "summary": "..." }',
].join('\n');
export const A6B_TEMPLATE = defineTemplate('agent.a6b.options', '2026.09.20', A6B_PROMPT, ['gaps', 'options']);
function promptSlots(input, deterministicResult) {
    const gapLines = input.gaps.map((g) => `- Slot: ${g.slot_id} | Severity: ${g.severity} | Effort: ${g.effort_band}`);
    const optionLines = Object.entries(deterministicResult.options_by_slot).flatMap(([slotId, opts]) => opts.map((o) => `  - ${slotId}: ${o.product_name} (${o.fit_level})`));
    return {
        gaps: field('blueprint.inventory', gapLines.join('\n')),
        options: derive('blueprint.inventory', optionLines.join('\n'), ['blueprint.inventory']),
    };
}
//# sourceMappingURL=agent-options-advisor-service.js.map